import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { itemsApi } from '@/services/itemsApi';
import { categoryApi, unitApi, gstRateApi, brandApi, modelApi } from '@/services/masterDataApi';
import { ItemFilters, Item } from '@/types';
import AddItemModal from '@/components/items/AddItemModal';
import EditItemModal from '@/components/items/EditItemModal';
import ViewItemModal from '@/components/items/ViewItemModal';
import ImportItemsModal from '@/components/items/ImportItemsModal';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Package,
  Filter,
  Upload,
  History,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency as formatActiveCurrency } from '@/utils/currency';
import SimpleSelect from '@/components/common/SimpleSelect';

const formatCurrency = (value?: number) => {
  if (!value) return 'N/A';
  return formatActiveCurrency(value);
};

export default function ItemsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [filters, setFilters] = useState<ItemFilters>({
    search: '',
    categoryId: undefined,
    brandId: undefined,
    modelId: undefined,
    isActive: undefined,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [viewItemId, setViewItemId] = useState<string | null>(null);

  // Fetch items
  const { data, isLoading } = useQuery({
    queryKey: ['items', filters, page, limit],
    queryFn: () => itemsApi.getAllItems({
      ...filters,
      page,
      limit,
    }),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Fetch master data for dropdowns
  const { data: categories } = useQuery({
    queryKey: ['categories-dropdown'],
    queryFn: () => categoryApi.getAll({ limit: 100, isActive: true }),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const { data: brands } = useQuery({
    queryKey: ['brands-dropdown'],
    queryFn: () => brandApi.getAll({ limit: 100, isActive: true }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: models } = useQuery({
    queryKey: ['models-dropdown', filters.brandId],
    queryFn: () => modelApi.getAll({
      limit: 100,
      isActive: true,
      brandId: filters.brandId
    }),
    enabled: !!filters.brandId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: units } = useQuery({
    queryKey: ['units-dropdown'],
    queryFn: () => unitApi.getAll({ limit: 100, isActive: true }),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const { data: gstRates } = useQuery({
    queryKey: ['gst-rates-dropdown'],
    queryFn: () => gstRateApi.getAll({ limit: 100, isActive: true }),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: itemsApi.deactivateItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  const handleDelete = async (id: string, name: string) => {
    if (
      window.confirm(
        `Are you sure you want to deactivate "${name}"? This item will no longer be available for new orders.`
      )
    ) {
      try {
        await deleteMutation.mutateAsync(id);
        alert('Item deactivated successfully');
      } catch (error: any) {
        alert(error.response?.data?.message || 'Failed to deactivate item');
      }
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Items Catalog</h1>
            <p className="text-gray-600 text-sm sm:text-base mt-0.5 sm:mt-1">
              Manage company-wide product catalog
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all text-sm"
            >
              <Filter className="h-4 w-4" />
              <span className="hidden sm:inline">{showFilters ? 'Hide' : 'Show'} Filters</span>
              <span className="sm:hidden">Filter</span>
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all shadow-sm text-sm"
            >
              <Upload className="h-4 w-4" />
              <span className="hidden sm:inline">Import Items</span>
              <span className="sm:hidden">Import</span>
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg hover:from-blue-700 hover:to-blue-600 transition-all shadow-sm text-sm"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Add Item</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by item name, code, HSN..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <SimpleSelect
                  value={filters.categoryId || ''}
                  onChange={(v) =>
                    setFilters({
                      ...filters,
                      categoryId: v || undefined,
                    })
                  }
                  options={[
                    { value: '', label: 'All Categories' },
                    ...(categories?.data ?? []).map((cat) => ({ value: cat.id, label: cat.name })),
                  ]}
                  placeholder="All Categories"
                  aria-label="Filter by category"
                  wrapperClassName="w-full min-w-0"
                  className="focus:!ring-blue-500 focus:!border-blue-500"
                />

                <SimpleSelect
                  value={filters.brandId || ''}
                  onChange={(v) =>
                    setFilters({
                      ...filters,
                      brandId: v || undefined,
                      modelId: undefined, // Reset model when brand changes
                    })
                  }
                  options={[
                    { value: '', label: 'All Brands' },
                    ...(brands?.data ?? []).map((brand) => ({ value: brand.id, label: brand.name })),
                  ]}
                  placeholder="All Brands"
                  aria-label="Filter by brand"
                  wrapperClassName="w-full min-w-0"
                  className="focus:!ring-blue-500 focus:!border-blue-500"
                />

                <SimpleSelect
                  value={filters.modelId || ''}
                  onChange={(v) =>
                    setFilters({
                      ...filters,
                      modelId: v || undefined,
                    })
                  }
                  disabled={!filters.brandId}
                  options={[
                    { value: '', label: 'All Models' },
                    ...(models?.data ?? []).map((model) => ({ value: model.id, label: model.name })),
                  ]}
                  placeholder="All Models"
                  aria-label="Filter by model"
                  wrapperClassName="w-full min-w-0"
                  className="focus:!ring-blue-500 focus:!border-blue-500"
                />
              </div>

              <div className="flex justify-end items-center">
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
                >
                  Apply Filters
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : !data?.items || data.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Package className="h-16 w-16 mb-4 opacity-50" />
              <p className="text-lg font-medium">No items found</p>
              <p className="text-sm">Add your first item to the catalog to get started</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-gray-200">
                {data.items.map((item) => (
                  <div key={item.id} className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 text-sm">{item.itemName}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{item.itemCode}{item.modelVariant ? ` | ${item.modelVariant}` : ''}</div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => { setViewItemId(item.id); setShowViewModal(true); }}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/items/${item.id}?tab=history`)}
                          className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg"
                          title="Transaction History"
                        >
                          <History className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => { setSelectedItem(item); setShowEditModal(true); }}
                          className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded-lg"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id, item.itemName)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs">
                      <span className="text-gray-600">{item.itemCategory?.name || 'N/A'}</span>
                      <span className="text-gray-400">|</span>
                      <span className="text-gray-600">{item.itemBrand?.name || 'N/A'}{item.itemModel ? ` - ${item.itemModel.name}` : ''}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs">
                      <span className="text-gray-500">{item.itemUnit?.name || 'N/A'}</span>
                      <span className="font-medium text-gray-900">{formatCurrency(item.salesPrice)}</span>
                      {item.hsnCode && <span className="text-gray-400">HSN: {item.hsnCode}</span>}
                      <span className="text-gray-400">{item._count?.branchInventories || 0} branches</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item Info
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category/Brand
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unit
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Purchase Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sales Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        HSN/GST
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Branches
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.items.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-medium text-gray-900">
                              {item.itemName}
                            </div>
                            <div className="text-sm text-gray-500">{item.itemCode}</div>
                            {item.barcode && (
                              <div className="text-xs text-gray-400">Barcode: {item.barcode}</div>
                            )}
                            {item.modelVariant && (
                              <div className="text-xs text-gray-400">{item.modelVariant}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm text-gray-900">
                              {item.itemCategory?.name || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {item.itemBrand?.name || 'N/A'}
                              {item.itemModel && ` - ${item.itemModel.name}`}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {item.itemUnit?.name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {formatCurrency(item.purchasePrice)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(item.salesPrice)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm text-gray-900">{item.hsnCode || 'N/A'}</div>
                            <div className="text-sm text-gray-500">
                              {item.itemGSTRate ? `GST: ${item.itemGSTRate.rate}%` : 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {item._count?.branchInventories || 0} branches
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => {
                                setViewItemId(item.id);
                                setShowViewModal(true);
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <Eye className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => navigate(`/items/${item.id}?tab=history`)}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="Transaction History"
                            >
                              <History className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedItem(item);
                                setShowEditModal(true);
                              }}
                              className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(item.id, item.itemName)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Deactivate"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {data && data.pagination.totalPages > 1 && (
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
                    <div className="text-xs sm:text-sm text-gray-700">
                      Showing {(page - 1) * limit + 1} to{' '}
                      {Math.min(page * limit, data.pagination.total)} of{' '}
                      {data.pagination.total} items
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPage(page - 1)}
                        disabled={page === 1}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={page === data.pagination.totalPages}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddItemModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {showEditModal && selectedItem && (
        <EditItemModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedItem(null);
          }}
          item={selectedItem}
        />
      )}

      {showViewModal && viewItemId && (
        <ViewItemModal
          itemId={viewItemId}
          onClose={() => {
            setShowViewModal(false);
            setViewItemId(null);
          }}
        />
      )}

      {showImportModal && (
        <ImportItemsModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
        />
      )}
    </>
  );
}
